import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  useAreasActions,
  useGeoAreasActions,
  useYupValidationResolver,
} from "@ryde/redux";
import {
  castErrorResult,
  createErrorResult,
  createSuccessResult,
  DrawerType,
  errorAndToastSimpleResult,
  errors as errorsKeys,
  GET_GEO_AREAS_GEOM_DELAY_MS,
  IGeoAreaGeom,
  IImportAreasProps,
  ImportAreaTypeEnum,
  IRydeProcess,
  RydeProcessTitle,
  SimpleResult,
  simpleResultErrorEmptyResponse,
  useDrawers,
} from "@ryde/shared";

import {
  IImportAreasForm,
  importAreasValidationSchema,
} from "./import-areas.models";

export const useImportAreasDrawer = () => {
  const { t } = useTranslation();
  const resolver = useYupValidationResolver(
    importAreasValidationSchema,
    "geoArea",
  );

  const { closeDrawer, openDrawer } = useDrawers();
  const { bulkImportFromGeoAreas } = useAreasActions();
  const { fetchGeoAreasGeom } = useGeoAreasActions();
  const [geoAreasGeom, setGeoAreasGeom] = useState<IGeoAreaGeom[]>([]);

  const methods = useForm<IImportAreasForm>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: { geoAreasIds: [] },
    resolver: resolver,
  });

  const {
    getValues,
    formState: { errors, isValid, touchedFields },
  } = methods;

  const isSaveDisabled = Boolean(
    !isValid || Object.keys(errors || {}).length || !touchedFields.geoAreasIds,
  );

  const geoAreasIdsValue = getValues("geoAreasIds");

  const onSaveClick = useEvent(
    async ({
      importType,
      onSelectPolygons,
    }: {
      importType: ImportAreaTypeEnum;
      onSelectPolygons: IImportAreasProps["onSelectPolygons"];
    }): Promise<SimpleResult<void>> => {
      if (!isValid) {
        return createErrorResult(errorsKeys.common.validationError);
      }

      const values = getValues();

      if (importType === ImportAreaTypeEnum.INSERT_AREAS) {
        const bulkImportResult = await bulkImportFromGeoAreas({
          geoAreasIds: values.geoAreasIds,
        });

        if (!bulkImportResult) {
          return simpleResultErrorEmptyResponse(t);
        }

        if (!bulkImportResult?.success) {
          return castErrorResult(bulkImportResult);
        }

        closeDrawer(DrawerType.ImportAreas);

        const resultData = bulkImportResult?.data as IRydeProcess;

        openDrawer<DrawerType.ProcessDrawer>({
          drawerProps: {
            type: DrawerType.ProcessDrawer,
            processId: resultData.id,
            processTitle: RydeProcessTitle.BulkImport,
            entity: "area",
          },
        });
      } else if (importType === ImportAreaTypeEnum.IMPORT_AREAS_POLYGONS) {
        if (onSelectPolygons) {
          const result = await fetchGeoAreasGeom({
            geoAreasIds: values.geoAreasIds,
          });

          if (result.success && result.data) {
            onSelectPolygons(result.data);
          } else {
            errorAndToastSimpleResult(result, t);
          }
        }

        closeDrawer(DrawerType.ImportAreas);
      }

      return createSuccessResult();
    },
  );

  const getGeoAreasGeom = useEvent(
    async ({
      geoAreasIds,
    }: {
      geoAreasIds: number[];
    }): Promise<SimpleResult<void>> => {
      const result = await fetchGeoAreasGeom({ geoAreasIds });

      if (result.success && result.data) {
        setGeoAreasGeom(result.data);
      } else {
        errorAndToastSimpleResult(result, t);
      }

      return createSuccessResult();
    },
  );

  const throttledGetGeoAreasGeom = useMemo(
    () => debounce(getGeoAreasGeom, GET_GEO_AREAS_GEOM_DELAY_MS),
    [getGeoAreasGeom],
  );

  useEffect(() => {
    throttledGetGeoAreasGeom({ geoAreasIds: geoAreasIdsValue });
  }, [geoAreasIdsValue, throttledGetGeoAreasGeom]);

  return {
    methods,
    errors,
    isSaveDisabled,
    geoAreasGeom,
    onSaveClick,
  };
};
